import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setAppError, setBreadCrumb } from "../../../../store/userSlice";
import SmartDownloadPagination from "../../../../components/SmartDataTable/SmartDownloadPagination";
import UploadFilePopUp_New from "../../../../ReusableComponents/UploadFile/UploadFileNew";
import nodeinstance from "../../../../nodeInstance";
import { AiOutlineClose } from "react-icons/ai";
import { CgSpinner } from "react-icons/cg";

// Column definitions
const BORDER_COLUMNS = [
  { key: "serial",               title: "S.NO.",                 searchable: false },
  { key: "point_of_hire",        title: "POINT OF HIRE",         searchable: true  },
  { key: "employee_id",          title: "EMPLOYEE ID",           searchable: true  },
  { key: "visa_provider",        title: "VISA PROVIDER",         searchable: true  },
  { key: "Client_Name",          title: "CLIENT NAME",           searchable: true  },
  { key: "visa_reference_number",title: "VISA REFERENCE NUMBER", searchable: true  },
  { key: "visa_stamping_date",   title: "VISA STAMPING DATE",    searchable: true  },
  { key: "border_number",        title: "BORDER NUMBER",         searchable: true  },
  { key: "border_status",        title: "STATUS",                searchable: true  },
  { key: "action",               title: "ACTION",                searchable: false },
];

export default function BorderNumber() {
  const dispatch = useDispatch();
  const token = localStorage.getItem("nodeToken");

  const [trigger, setTrigger] = useState(null);
  const [uploadPopup, setUploadPopup] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    dispatch(setBreadCrumb([{ label: "Border Number", link: "" }]));
  }, [dispatch]);

  // Download the border number Excel template
  const downloadTemplate = useCallback(async () => {
    setDownloadLoading(true);
    try {
      const response = await nodeinstance.get("/employees/downloadBorderNumberExcel", {
        responseType: "blob",
      });
      if (response.status === 200) {
        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Border_Number_Template.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
        dispatch(setAppError({ msg: "Template Downloaded", color: "success" }));
      }
    } catch (err) {
      dispatch(setAppError({ msg: err?.message || "Download failed" }));
    } finally {
      setDownloadLoading(false);
    }
  }, [dispatch]);

  // Save inline border number edit
  const handleSave = useCallback(async (item) => {
    const borderNumber = editValue?.trim() || item.border_number;
    if (!borderNumber) return;
    try {
      const res = await nodeinstance.post("/employees/updateBorderNumber/", {
        borderNumber: borderNumber.toUpperCase(),
        employeeId: item.employee_id,
        pointHire: item.point_of_hire,
      });
      if (res?.data?.status === true) {
        dispatch(setAppError({ msg: res.data.message, color: "success" }));
        setEditingId(null);
        setTrigger((prev) => !prev); // invalidate cache & refetch
      } else {
        dispatch(setAppError({ msg: res?.data?.error }));
      }
    } catch (err) {
      dispatch(setAppError({ msg: err?.message }));
    }
  }, [editValue, dispatch]);

  const handleDownloadComplete = useCallback((result) => {
    dispatch(setAppError({ msg: `Excel Downloaded (${result.recordCount} records)`, color: "success" }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({ msg: error.error || "Download failed" }));
  }, [dispatch]);

  // Custom cell renderer
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    switch (column.key) {
      case "serial":
        return (currentPage - 1) * pageSize + rowIndex + 1;

      case "border_status":
        return (
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
            item.border_status === "Border Number Updated"
              ? "bg-green-100 text-green-700"
              : "bg-red/10 text-red"
          }`}>
            {item.border_status || "-"}
          </span>
        );

      case "action": {
        const isEditing = editingId === item.employee_id;
        return (
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <input
                  autoFocus
                  defaultValue={item.border_number || ""}
                  maxLength={10}
                  className="input input-sm input-bordered w-32"
                  onChange={(e) => setEditValue(e.target.value)}
                />
                <button
                  className="px-2 py-1 rounded-md bg-green-500 text-white text-xs"
                  onClick={() => handleSave(item)}
                >
                  Save
                </button>
                <button onClick={() => setEditingId(null)}>
                  <AiOutlineClose />
                </button>
              </>
            ) : (
              <button
                className={`px-2 py-1 rounded-md text-white text-xs w-16 ${
                  item.border_status === "Border Number Updated"
                    ? "bg-green-500"
                    : "bg-red"
                }`}
                onClick={() => {
                  setEditingId(item.employee_id);
                  setEditValue(item.border_number || "");
                }}
              >
                {item.border_status === "Border Number Updated" ? "Edit" : "Update"}
              </button>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  }, [editingId, editValue, handleSave]);

  const additionalActions = useMemo(() => [
    {
      label: downloadLoading ? "Downloading..." : "Download Template",
      onClick: downloadTemplate,
      disabled: downloadLoading,
      className: "p-2 px-4 rounded-md bg-blue text-white min-w-fit text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2",
      icon: downloadLoading ? <CgSpinner className="animate-spin" /> : null,
    },
    {
      label: "Upload List",
      onClick: () => setUploadPopup(true),
      className: "p-2 px-4 rounded-md bg-blue text-white min-w-fit text-sm",
    },
  ], [downloadTemplate, downloadLoading]);

  return (
    <div className="m-3">
      <SmartDownloadPagination
        apiEndpoint="/employees"
        downloadEndpoint={null}
        token={token}

        columns={BORDER_COLUMNS}
        enableHighlighting={true}

        defaultPageSize={50}
        pageSizeOptions={[10, 25, 50, 100, 500]}

        enableDownload={false}

        enableSearch={true}
        searchPlaceholder="Search by Employee ID, Visa Provider, Client, Visa Ref, Border No..."
        searchDebounceMs={400}

        trigger={trigger}

        renderCustomCell={renderCustomCell}
        additionalActions={additionalActions}

        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}

        noDataMessage="No border number records found."
      />

      {uploadPopup && (
        <UploadFilePopUp_New
          PopUp={uploadPopup}
          setPopUp={setUploadPopup}
          title="Update Border Number"
          url="/employees/updateBorderNumbersFromExcel"
          callBack={() => setTrigger((prev) => !prev)}
          setTrigger={setTrigger}
          trigger={trigger}
        />
      )}
    </div>
  );
}
